import * as React from "react";
import { cn } from "../lib/utils";
import { Badge, badgeVariants } from "./badge";
import { cva } from "class-variance-authority";

const statusBadgeVariants = cva("inline-flex items-center gap-1.5", {
  variants: {
    status: {
      // Success
      available: "bg-success/15 text-success border-success/30",
      active: "bg-success/15 text-success border-success/30",
      completed: "bg-success/15 text-success border-success/30",
      approved: "bg-success/15 text-success border-success/30",
      paid: "bg-success/15 text-success border-success/30",
      done: "bg-success/15 text-success border-success/30",

      // Info
      rented: "bg-info/15 text-info border-info/30",
      confirmed: "bg-info/15 text-info border-info/30",
      new: "bg-info/15 text-info border-info/30",
      open: "bg-info/15 text-info border-info/30",
      in_progress: "bg-info/15 text-info border-info/30",
      assigned: "bg-info/15 text-info border-info/30",
      scheduled: "bg-info/15 text-info border-info/30",
      submitted: "bg-info/15 text-info border-info/30",

      // Warning
      pending: "bg-warning/15 text-warning border-warning/30",
      waiting: "bg-warning/15 text-warning border-warning/30",
      review: "bg-warning/15 text-warning border-warning/30",
      under_review: "bg-warning/15 text-warning border-warning/30",
      disputed: "bg-warning/15 text-warning border-warning/30",

      // Primary / Draft
      todo: "bg-primary/10 text-primary border-primary/20",
      draft: "bg-primary/10 text-primary border-primary/20",
      planned: "bg-primary/10 text-primary border-primary/20",

      // Destructive
      declined: "bg-destructive/15 text-destructive border-destructive/30",
      cancelled: "bg-destructive/15 text-destructive border-destructive/30",
      blocked: "bg-destructive/15 text-destructive border-destructive/30",
      no_show: "bg-destructive/15 text-destructive border-destructive/30",
      overdue: "bg-destructive/15 text-destructive border-destructive/30",
      paused: "bg-destructive/15 text-destructive border-destructive/30",
      rejected: "bg-destructive/15 text-destructive border-destructive/30",

      // Muted / Neutral
      inactive: "bg-muted text-muted-foreground border-border",
      closed: "bg-muted text-muted-foreground border-border",
      skipped: "bg-muted text-muted-foreground border-border",
    },
  },
  defaultVariants: {
    status: "new",
  },
});

function resolveStatusKey(status) {
  if (!status) return "new";
  const s = String(status).trim().toLowerCase().replace(/[\s-]/g, "_");
  const known = [
    "available", "active", "completed", "approved", "paid", "done",
    "rented", "confirmed", "new", "open", "in_progress", "assigned", "scheduled", "submitted",
    "pending", "waiting", "review", "under_review", "disputed",
    "todo", "draft", "planned",
    "declined", "cancelled", "blocked", "no_show", "overdue", "paused", "rejected",
    "inactive", "closed", "skipped",
  ];
  if (known.includes(s)) return s;
  if (s.includes("prog") || s.includes("work")) return "in_progress";
  if (s.includes("pend") || s.includes("wait")) return "pending";
  if (s.includes("approv") || s.includes("paid") || s.includes("comp") || s.includes("done")) return "completed";
  if (s.includes("canc") || s.includes("declin") || s.includes("rej") || s.includes("block") || s.includes("fail")) return "declined";
  if (s.includes("disput") || s.includes("warn") || s.includes("urg")) return "disputed";
  if (s.includes("draft")) return "draft";
  if (s.includes("new") || s.includes("open") || s.includes("creat")) return "new";
  return "new";
}

const StatusBadge = React.forwardRef(
  ({ className, status, icon: Icon, children, ...props }, ref) => {
    const resolvedStatus = resolveStatusKey(status);
    return (
      <Badge
        ref={ref}
        variant="outline"
        className={cn(statusBadgeVariants({ status: resolvedStatus }), className)}
        {...props}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {children}
      </Badge>
    );
  },
);
StatusBadge.displayName = "StatusBadge";

export { StatusBadge };
